import reduce from 'lodash/reduce';
import styled from 'styled-components';

import BillingRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/BillingRegularSVG';
import CogLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/RebrandCogLightSVG';
import HiringEventsRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/HiringEventsRebrandRegularSVG';
import HubRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/HubRebrandRegularSVG';
import UserRolesRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/UserRolesRebrandRegularSVG';
import styles from '@jobcase/react_pattern_library/styles/rebrand';
import DashboardRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/NavigationCommunityLightSVG';

import { BASE_FILTER_PARAMS } from './constants';
import { doesRoleExist } from './grants';
import { EXPECTED_ROUTE_KEYS, EXPECTED_ROUTES, EXPECTED_ROUTES_TITLES, EMPLOYER_ROLES } from '../constants';
import { toQueryString } from './fetch';

const HubIcon = styled(HubRegularSVG)`
  height: ${styles.spacing.tiny + 5}px;
  margin-left: -${styles.spacing.extraExtraTiny / 2}px;
  margin-right: -${styles.spacing.extraExtraTiny - 1}px;
  width: ${styles.spacing.tiny + 5}px;
`;

const SettingsIcon = styled(CogLightSVG)`
  height: ${styles.spacing.tiny + 4}px;
  margin-left: -${styles.spacing.extraExtraTiny / 2}px;
  margin-right: -${styles.spacing.extraExtraTiny - 1}px;
  width: ${styles.spacing.tiny + 5}px;
`;

const UserRolesIcon = styled(UserRolesRegularSVG)`
  height: ${styles.spacing.tiny - 1}px;
  width: ${styles.spacing.tiny + 2}px;
`;

const HiringEventIcon = styled(HiringEventsRegularSVG)`
  height: ${styles.spacing.tiny + 2}px;
  width: ${styles.spacing.tiny}px;
`;

const DashboardIcon = styled(DashboardRegularSVG)`
  height: 18px;
  margin-left: -${styles.spacing.extraExtraTiny - 1}px;
  margin-right: -${styles.spacing.extraExtraTiny - 1}px;
  width: 22px;

  path {
    stroke: ${styles.color.jobcaseBlackColor};
  }
`;

const BillingIcon = styled(BillingRegularSVG)`
  height: ${styles.spacing.small - 2}px;
  width: ${styles.spacing.tiny}px;
`;

/**
 * Filter a query object to remove parameters such as odin or utm*
 *
 * @param {Object} params query params to filter.
 * @param {Array} additionalFilterParamsArr Array of regexes for filtering out more parameters than just the base parameters.
 * @return query params object after filtering.
 */
export const filterParams = (params, additionalFilterParamsArr = []) => {
  const allParamsToFilter = [...BASE_FILTER_PARAMS, ...additionalFilterParamsArr];
  return reduce(params, (result, value, key) => {
    const isMatch = allParamsToFilter.some(regex => key.match(regex));
    if (isMatch) {
      return result;
    }

    return { ...result, [key]: value };
  }, {});
};

export const push = (router, options = {}) => {
  router.push({
    pathname: options.pathname,
    query: filterParams(options.query),
  }, undefined, { shallow: !!options.shallow });
};

export const replace = (router, options = {}) => {
  if (options.forceReplace) {
    window.history.replaceState({}, '', `${options.pathname}${toQueryString(filterParams(options.query))}`);
  } else {
    router.replace({
      pathname: options.pathname,
      query: filterParams(options.query),
    }, undefined, { shallow: !!options.shallow });
  }
};

export const isRouteViewable = (route, employerGrants, isEmployerAdmin, orgAssociation) => {
  const isAdmin = isEmployerAdmin
    || doesRoleExist(employerGrants, EMPLOYER_ROLES.JOBCASE_EMPLOYER_ADMIN)
    || doesRoleExist(employerGrants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);

  if (!orgAssociation) {
    switch (route) {
      case EXPECTED_ROUTE_KEYS.SHOW_HIRING_EVENTS:
        return isAdmin || doesRoleExist(employerGrants, EMPLOYER_ROLES.HIRING_EVENT_MANAGER);
      case EXPECTED_ROUTE_KEYS.SHOW_USER_ROLES_PERMISSIONS:
        return isAdmin || doesRoleExist(employerGrants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
      case EXPECTED_ROUTE_KEYS.SHOW_HUBS:
        return isAdmin || doesRoleExist(employerGrants, EMPLOYER_ROLES.COMPANY_HUB_MANAGER);
      case EXPECTED_ROUTE_KEYS.SHOW_SETTINGS:
        return isAdmin || doesRoleExist(employerGrants, EMPLOYER_ROLES.SETTINGS_MANAGER);
      case EXPECTED_ROUTE_KEYS.SHOW_BILLING:
      case EXPECTED_ROUTE_KEYS.SHOW_DASHBOARD:
      case EXPECTED_ROUTE_KEYS.SHOW_MANAGE_BUDGETS:
      case EXPECTED_ROUTE_KEYS.SHOW_JOB_REPORTING:
        // Beta pages to only display to JAE users
        return doesRoleExist(employerGrants, EMPLOYER_ROLES.JOBCASE_EMPLOYER_ADMIN) || doesRoleExist(employerGrants, EMPLOYER_ROLES.JOB_REPORTING);
      default:
        return false;
    }
  }
  if (isAdmin) return true;
  switch (route) {
    case EXPECTED_ROUTE_KEYS.SHOW_HIRING_EVENTS:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.HIRING_EVENT_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_USER_ROLES_PERMISSIONS:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
    case EXPECTED_ROUTE_KEYS.SHOW_HUBS:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.COMPANY_HUB_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_DASHBOARD:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.JOBS_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_SETTINGS:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.SETTINGS_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_BILLING:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.BILLING_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_MANAGE_BUDGETS:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.BUDGET_MANAGER);
    case EXPECTED_ROUTE_KEYS.SHOW_JOB_REPORTING:
      return doesRoleExist(employerGrants, EMPLOYER_ROLES.JOB_REPORTING);
    default:
      return false;
  }
};

export const routeIcons = (route) => {
  const icons = {
    show_dashboard: DashboardIcon,
    show_billing: BillingIcon,
    show_hiring_events: HiringEventIcon,
    show_user_roles_permissions: UserRolesIcon,
    show_hubs: HubIcon,
    show_manage_budgets: UserRolesIcon,
    show_job_reporting: UserRolesIcon,
    show_settings: SettingsIcon,
  };
  return icons[route] || null;
};

export const dynamicRoute = (employerGrants, allowAll = false, orgAssociation = false) => {
  const routes = EXPECTED_ROUTES.map((route) => {
    const isViewable = isRouteViewable(route, employerGrants, allowAll, orgAssociation);
    return {
      key: route,
      icon: routeIcons(route),
      isViewable,
      title: EXPECTED_ROUTES_TITLES[route],
    };
  });

  if (orgAssociation && allowAll) return routes;
  return routes.filter(route => route.isViewable);
};

export const isModuleViewable = (employerGrants, module) => {
  const isAdmin = doesRoleExist(employerGrants, EMPLOYER_ROLES.JOBCASE_EMPLOYER_ADMIN) || doesRoleExist(employerGrants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  if (isAdmin) return true;
  return doesRoleExist(employerGrants, module);
};

export default {
  dynamicRoute,
  filterParams,
  isModuleViewable,
  isRouteViewable,
  push,
  replace,
};
